import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderOpen, ArrowLeft, Camera, Image as ImageIcon, Video, Sparkles, Archive } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import RequireAuthModal from '../components/auth/RequireAuthModal';
import LoginModal from '../components/auth/LoginModal';
import { useStoreConfig } from '../hooks/useStoreConfig';
import MyImagesModal from '../components/MyImagesModal';
import MyAIModelsModal from '../components/MyAIModelsModal';
import MyAIVideosModal from '../components/MyAIVideosModal';
import AllFilesModal from '../components/AllFilesModal';

const MyFile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const { storeConfig, loading: loadingStoreConfig } = useStoreConfig();
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [showAIModelsModal, setShowAIModelsModal] = useState(false);
  const [showAIVideosModal, setShowAIVideosModal] = useState(false);
  const [showAllFilesModal, setShowAllFilesModal] = useState(false);
  const [showRequireAuth, setShowRequireAuth] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const buttonVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  const handleStoreTypeSelected = (type: string) => {
    // El hook se encargará de recargar automáticamente
    console.log('Tipo de tienda seleccionado:', type);
  };

  const handleImagesClick = () => {
    if (!user) {
      setShowRequireAuth(true);
      return;
    }
    setShowImagesModal(true);
  };

  const handleAIModelsClick = () => {
    if (!user) {
      setShowRequireAuth(true);
      return;
    }
    setShowAIModelsModal(true);
  };

  const handleAIVideosClick = () => {
    if (!user) {
      setShowRequireAuth(true);
      return;
    }
    setShowAIVideosModal(true);
  };

  const handleAllFilesClick = () => {
    if (!user) {
      setShowRequireAuth(true);
      return;
    }
    setShowAllFilesModal(true);
  };

  return (
    <div className="min-h-screen px-4 pt-20 pb-24">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 mb-6"
          >
            <ArrowLeft size={20} />
            <span>{t('back')}</span>
          </button>
          
          <h1 className="text-3xl md:text-4xl font-contrail font-bold text-gray-900 dark:text-gray-100">
            {t('myFile')}
          </h1>
        </motion.div>

        {/* Main Buttons */}
        <motion.div
          variants={itemVariants}
          className="space-y-6"
        >
          {/* Renglón superior */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={handleImagesClick}
              className="card p-6 text-left group hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <ImageIcon className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Mis imágenes
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Gestiona todas tus imágenes de productos organizadas por categorías
              </p>
            </motion.button>

            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={handleAIModelsClick}
              className="card p-6 text-left group hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Mis modelos IA
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Visualiza y gestiona todas las imágenes generadas con inteligencia artificial
              </p>
            </motion.button>

            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={handleAIVideosClick}
              className="card p-6 text-left group hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
                  <Video className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Mis videos IA
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Reproduce y gestiona todos los videos generados con inteligencia artificial
              </p>
            </motion.button>
          </div>

          {/* Renglón inferior - Todo mi archivo */}
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={handleAllFilesClick}
            className="card p-8 text-left group hover:shadow-xl transition-all duration-300 w-full"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <Archive className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Todo mi archivo
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Accede a todo tu contenido multimedia en una sola vista: imágenes, modelos IA y videos IA organizados por producto
            </p>
          </motion.button>
        </motion.div>


        {/* Modals */}
        <AnimatePresence>
          <MyImagesModal
            isOpen={showImagesModal}
            onClose={() => setShowImagesModal(false)}
          />
          
          <MyAIModelsModal
            isOpen={showAIModelsModal}
            onClose={() => setShowAIModelsModal(false)}
          />
          
          <MyAIVideosModal
            isOpen={showAIVideosModal}
            onClose={() => setShowAIVideosModal(false)}
          />
          
          <AllFilesModal
            isOpen={showAllFilesModal}
            onClose={() => setShowAllFilesModal(false)}
          />
        </AnimatePresence>

        <RequireAuthModal
          isOpen={showRequireAuth}
          onClose={() => setShowRequireAuth(false)}
          onLoginClick={() => { setShowRequireAuth(false); setShowLoginModal(true); }}
        />
        <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      </motion.div>
    </div>
  );
};

export default MyFile;
